import type { ISeeMoreTeacher } from '@/types/teacher';

import { useRef, useMemo, useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import { IconBook, IconMail, IconSearch } from '@tabler/icons-react';

import { UserAvatarList } from '@/components/Avatar';
import EmptyState from '@/components/EmptyState';
import Pagination from '@/components/Pagination';
import TeachersClassesSkeleton from '@/components/SkeletonLoader/TeachersClassesSkeleton';
import TeachersGridSkeleton from '@/components/SkeletonLoader/TeachersGridSkeleton';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { getSeeMoreTeachers } from '@/service/teacherService';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { getAssetUrl } from '@/utils';

const PAGE_LIMIT = 10;

const ClassTeachersList = () => {
  const { formatText } = useFormatText();
  const { setIsBreadcrumb } = usePageTitle();
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const className =
    (location.state as { className?: string } | null)?.className || 'Class';

  const [searchTerm, setSearchTerm] = useState('');
  const [teachers, setTeachers] = useState<ISeeMoreTeacher[]>([]);
  const [totalTeachers, setTotalTeachers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { isScreenLoading, setScreenLoading, isTableLoading, setTableLoading } =
    useSkeletonStore();
  const initialLoadRef = useRef(false);
  const { settings } = useSettingsStore();

  const debouncedSearch = useDebounce(searchTerm, 500);

  const breadcrumbPath = useMemo(
    () => [
      {
        label: formatText('breadcrumb.teachers'),
        to: '/school/teachers-classes',
      },
      {
        label: className,
        to: classId
          ? `/school/teachers-classes/class-details/${classId}`
          : '/school/teachers-classes',
        state: { className },
      },
      { label: formatText('classDetails.teachers.title') },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [classId, className, settings]
  );

  useEffect(() => {
    setIsBreadcrumb(breadcrumbPath);
    return () => {
      setIsBreadcrumb([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breadcrumbPath]);

  useEffect(() => {
    const fetchTeachers = async () => {
      if (!classId) return;

      if (!initialLoadRef.current) {
        setScreenLoading(true);
      } else {
        setTableLoading(true);
      }

      try {
        const response = await getSeeMoreTeachers({
          class_id: classId,
          limit: PAGE_LIMIT,
          offset: (currentPage - 1) * PAGE_LIMIT,
          created_at: 'DESC',
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        });

        if (response?.status && Array.isArray(response.data)) {
          setTeachers(response.data);
          setTotalTeachers(response.total ?? response.data.length);
        } else {
          setTeachers([]);
          setTotalTeachers(0);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setScreenLoading(false);
        setTableLoading(false);
        initialLoadRef.current = true;
      }
    };

    fetchTeachers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, debouncedSearch, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  return (
    <div className="space-y-6" data-testid="class-teachers-list-container">
      <div className="w-87.5">
        <div className="w-full border border-neutral-light-200 rounded-lg px-3 py-2.5 shadow-small flex items-center gap-2 bg-white">
          <IconSearch size={18} className="text-neutral-disabled" />
          <input
            type="text"
            placeholder={formatText('classDetails.teachers.searchPlaceholder')}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="w-full bg-transparent border-none outline-none text-sm text-neutral-primary placeholder:text-neutral-disabled"
            data-testid="teacher-search-input"
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-neutral-primary">
              {formatText('classDetails.teachers.title')}
            </h2>
            <div
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-neutral-100"
              data-testid="teachers-count"
            >
              <span className="text-xs font-semibold text-neutral-primary">
                {String(totalTeachers).padStart(2, '0')}
              </span>
            </div>
          </div>
        </div>

        {isScreenLoading && <TeachersClassesSkeleton />}
        {!isScreenLoading && isTableLoading && <TeachersGridSkeleton />}
        {!isScreenLoading && !isTableLoading && teachers.length === 0 && (
          <EmptyState
            title={formatText('common.no-data-found')}
            className="h-75"
          />
        )}
        {!isScreenLoading && !isTableLoading && teachers.length > 0 && (
          <div
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4"
            data-testid="teachers-grid"
          >
            {teachers.map((teacher) => (
              <div
                key={teacher.id}
                className="border border-neutral-light-200 rounded-2xl p-4 space-y-4 bg-white cursor-pointer hover:border-neutral-200 hover:shadow-small transition"
                role="button"
                tabIndex={0}
                data-testid={`teacher-card-${teacher.id}`}
                onClick={() =>
                  navigate(
                    `/school/teachers-classes/teacher-details/${teacher.id}`
                  )
                }
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(
                      `/school/teachers-classes/teacher-details/${teacher.id}`
                    );
                  }
                }}
              >
                <div className="flex items-center gap-4">
                  <UserAvatarList
                    user={teacher.name}
                    image={getAssetUrl(teacher?.profile_pic)}
                    className="w-10 h-10 min-w-10"
                    dataTestId={`teacher-avatar-${teacher.id}`}
                  />
                  <h3
                    className="text-lg font-semibold text-neutral-primary leading-7 tracking-tight truncate"
                    data-testid={`teacher-name-${teacher.id}`}
                  >
                    {teacher.name}
                  </h3>
                </div>

                <div className="flex gap-2">
                  <IconMail
                    size={16}
                    className="text-neutral-secondary mt-0.5"
                  />
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <p className="text-xs text-neutral-secondary">
                      {formatText('classDetails.teachers.email')}
                    </p>
                    <p
                      className="text-sm text-neutral-primary truncate"
                      data-testid={`teacher-email-${teacher.id}`}
                    >
                      {teacher.email}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <IconBook
                    size={16}
                    className="text-neutral-secondary mt-0.5"
                  />
                  <div className="flex flex-col gap-0.5">
                    <p className="text-xs text-neutral-secondary">
                      {formatText('classDetails.teachers.lessonsCreated')}
                    </p>
                    <p
                      className="text-sm font-semibold text-pink-600"
                      data-testid={`teacher-lessons-count-${teacher.id}`}
                    >
                      {teacher.lessons_created_count}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {totalTeachers > PAGE_LIMIT && (
          <Pagination
            total={totalTeachers}
            currentPage={currentPage}
            pageSize={PAGE_LIMIT}
            setCurrentPage={setCurrentPage}
            className="border-t-0!"
          />
        )}
      </div>
    </div>
  );
};

export default ClassTeachersList;
